import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import { Formik } from 'formik';
import { axiosPath, path } from '@/routing/helpers/router';
import * as currentPageAction from '@/store/actions/currentPageAction';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import { AppCheckbox, AppRemixIcon } from '@/layout/AppComponents';

const DIGITS = 6;

export default function Verify2faScreen() {
  const { method, challenge } = useParams();
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const [error, setError] = useState();

  const handleVerify = async (values) => {
    await axios
      .post(axiosPath('api_r_user_2fa_verify',true), values)
      .then(async ({ data }) => {
        if (isMounted.current) {
          if (data['2fa_required']) {
            navigate(
              path({
                name: 'user_2fa_challenge',
                params: { method: data.method, challenge: data.challenge },
              }),
            );
          } else {
            await localStorage.setItem('t', data.token);
            await localStorage.setItem('rt', data.refresh_token);
            dispatch(currentPageAction.change('recentlyLoggedIn', true, true));
            navigate(
              path({
                name: 'user_home',
              }),
            );
          }
        }
      })
      .catch((e) => setError(e.response?.data?.error));
  };

  const handleChange = (e, index, values, setFieldValue) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;

    const next = [...values.code];

    // Buscar el primer hueco vacío desde el índice actual
    let targetIndex = index;
    while (targetIndex < DIGITS && next[targetIndex]) {
      targetIndex++;
    }

    // Si no hay hueco, no hacemos nada
    if (targetIndex >= DIGITS) return;

    next[targetIndex] = val[0];
    setFieldValue('code', next);

    // Mover foco al siguiente
    if (targetIndex < DIGITS - 1) {
      inputsRef.current[targetIndex + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index, values, setFieldValue) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const next = [...values.code];

      if (next[index]) {
        next[index] = '';
        setFieldValue('code', next);
        return;
      }

      if (index > 0) {
        next[index - 1] = '';
        setFieldValue('code', next);
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e, setFieldValue) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, DIGITS);

    if (pasted.length !== DIGITS) return;

    setFieldValue('code', pasted.split(''));
    inputsRef.current[DIGITS - 1]?.focus();
  };

  return (
    <Container>
      <div className="twofactor-form">
        <Formik
          initialValues={{
            code: Array(DIGITS).fill(''),
            method,
            challenge,
            trusted: false,
          }}
          onSubmit={(values) =>
            handleVerify({
              challenge: values.challenge,
              code: values.code.join(''),
              trusted: values.trusted,
              method: values.method,
            })
          }
        >
          {({ handleSubmit, values, setFieldValue, isSubmitting }) => (
            <form
              onSubmit={handleSubmit}
              className="two-factor-form position-relative"
            >
              <h1>
                <AppRemixIcon icon="lock-password" filled />
              </h1>
              {method === 'g' && (
                <p>
                  Introduce tu código de un solo uso de tu APP de autenticación.
                </p>
              )}
              {method === 'e' && (
                <p>
                  Introduce tu código de un solo uso que te enviamos por Email.
                </p>
              )}

              <div className="digit-group">
                {values.code.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="form-control text-center"
                    style={{ width: 45, fontSize: 24 }}
                    value={digit}
                    onChange={(e) => handleChange(e, i, values, setFieldValue)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, i, values, setFieldValue)
                    }
                    onPaste={(e) => handlePaste(e, setFieldValue)}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <AppCheckbox
                value={values.trusted}
                onChange={(v) => setFieldValue('trusted', v)}
                title="Función no disponible temporalmente"
              >
                Es un dispositivo de confianza{' '}
                <small>
                  (Se recordará este dispositivo durante 7 días)
                </small>{' '}
              </AppCheckbox>

              <button
                type="submit"
                className="btn btn-primary btn-block text-center"
                disabled={isSubmitting || values.code.some((d) => !d)}
              >
                {isSubmitting ? (
                  <div className="loaderContainer">
                    <div className="loader"></div>
                  </div>
                ) : (
                  'Verificar'
                )}
              </button>
              {error && (
                <ErrorContainer className="alert alert-danger">
                  {error}
                </ErrorContainer>
              )}
            </form>
          )}
        </Formik>
      </div>
    </Container>
  );
}

const Container = styled.div`
  .twofactor-form {
    width: 550px;
    height: 100vh;
    margin: 0 auto;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
  }
  .two-factor-form {
    padding: 10px 20px 20px 20px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    h1 {
      margin: 0;
      text-align: center;
      font-size: 52px;
      color: #003d47;
    }
    p {
      text-align: center;
      color: #003d47;
      font-size: 16px;
      margin-bottom: 40px;
    }
    .digit-group {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 20px;
    }
    input {
      width: 2em;
      height: 2.3em;
      font-size: 24px;
      text-align: center;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
    input:focus {
      border-color: #003d47;
      outline: none;
    }
    input:disabled {
      background: #eee;
    }
    .loaderContainer {
      display: flex;
      justify-content: center;
      .loader {
        width: 24px;
        aspect-ratio: 1;
        --c: no-repeat linear-gradient(#fff 0 0);
        background:
          var(--c) 0% 50%,
          var(--c) 50% 50%,
          var(--c) 100% 50%;
        background-size: 20% 100%;
        animation: l1 1s infinite linear;
      }
      @keyframes l1 {
        0% {
          background-size:
            20% 100%,
            20% 100%,
            20% 100%;
        }
        33% {
          background-size:
            20% 10%,
            20% 100%,
            20% 100%;
        }
        50% {
          background-size:
            20% 100%,
            20% 10%,
            20% 100%;
        }
        66% {
          background-size:
            20% 100%,
            20% 100%,
            20% 10%;
        }
        100% {
          background-size:
            20% 100%,
            20% 100%,
            20% 100%;
        }
      }
    }
  }
`;
const ErrorContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -80px;
`;
